/**
 * New subscription wizard — single page covering the target (a page or an
 * Atlas Report, ADR-0216), cadence, name,
 * active window, and one or more deliveries. Recipient
 * picker supports the same three kinds as edit (account / group / external
 * email) via the shared RecipientsEditor.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { LoadingState, ErrorState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { SubscriptionVariablesHelp } from "../components/SubscriptionVariablesHelp.js";
import {
  RecipientsEditor,
  recipientToWire,
  recipientsAreComplete,
  type Candidate,
  type DraftRecipient,
} from "../components/SubscriptionRecipientsEditor.js";

type PageOption = { id: string; title: string };
type PageWidget = { id: string; title: string; kind: string; parent_id: string | null };
type ReportOption = { id: string; name: string; active_version_no: number | null };
type TargetKind = "page" | "atlas_report";

/** The default subject for each target (the hub's templating defaults, ADR-0216 §4.4). */
function defaultSubject(kind: TargetKind): string {
  return kind === "atlas_report" ? "{{report_title}} — {{data_as_of}}" : "{{page_title}} — {{fired_at}}";
}

type Cadence = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";
const WEEKDAYS: Array<{ code: string; label: string }> = [
  { code: "mon", label: "Mon" },
  { code: "tue", label: "Tue" },
  { code: "wed", label: "Wed" },
  { code: "thu", label: "Thu" },
  { code: "fri", label: "Fri" },
  { code: "sat", label: "Sat" },
  { code: "sun", label: "Sun" },
];

type DraftDelivery = {
  name: string;
  contentKind: "whole_page" | "widgets" | "whole_report";
  widgetIds: string[];
  recipients: DraftRecipient[];
  subject: string;
  body: string;
};

function buildCadenceConfig(cadence: Cadence, time: string, weeklyDays: string[], monthlyDay: string, yearlyMonth: number, yearlyDay: number): Record<string, unknown> {
  switch (cadence) {
    case "DAILY":
      return { time };
    case "WEEKLY":
      return { time, days: weeklyDays };
    case "MONTHLY":
      return monthlyDay === "LAST" ? { time, day: "LAST" } : { time, day: Number.parseInt(monthlyDay, 10) };
    case "YEARLY":
      return { time, month: yearlyMonth, day: yearlyDay };
  }
}

export function TenantSubscriptionNew() {
  const nav = useNavigate();
  const toast = useToast();

  // Step 1: target + cadence + meta
  const [targetKind, setTargetKind] = useState<TargetKind>("page");
  const [pages, setPages] = useState<PageOption[]>([]);
  const [pageId, setPageId] = useState("");
  const [reports, setReports] = useState<ReportOption[]>([]);
  const [reportId, setReportId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [cadence, setCadence] = useState<Cadence>("DAILY");
  const [time, setTime] = useState("07:00");
  const [weeklyDays, setWeeklyDays] = useState<string[]>(["mon"]);
  const [monthlyDay, setMonthlyDay] = useState("1");
  const [yearlyMonth, setYearlyMonth] = useState(1);
  const [yearlyDay, setYearlyDay] = useState(15);
  const [activeFrom, setActiveFrom] = useState("");
  const [activeUntil, setActiveUntil] = useState("");

  // Step 2: deliveries
  const [widgets, setWidgets] = useState<PageWidget[]>([]);
  const [pageIsPublished, setPageIsPublished] = useState(true);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [deliveries, setDeliveries] = useState<DraftDelivery[]>([
    {
      name: "Everyone",
      contentKind: "whole_page",
      widgetIds: [],
      recipients: [],
      subject: "{{page_title}} — {{fired_at}}",
      body: "",
    },
  ]);

  const [loadingPages, setLoadingPages] = useState(true);
  const [pagesError, setPagesError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Load published pages
  useEffect(() => {
    void (async () => {
      try {
        // The hub has a dedicated published-pages endpoint that returns
        // `{ pages: PageNavItem[] }` (id + title + route_key + …). Use it
        // rather than /tenant/pages?status=published which carries the
        // larger PageListItem shape and the scoped tab/q filters we
        // don't need here.
        const data = await apiFetch<{ pages: Array<{ id: string; title: string }> }>(
          "/api/tenant/pages/published",
        );
        setPages((data.pages ?? []).map((p) => ({ id: p.id, title: p.title })));
      } catch (e) {
        setPagesError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoadingPages(false);
      }
    })();
  }, []);

  // ADR-0216: the Atlas Reports a subscription may deliver. One may be chosen
  // before a version is approved; it resumes once one is (spec §4.6).
  useEffect(() => {
    void (async () => {
      try {
        const data = await apiFetch<{ reports: Array<{ report_id: string; name: string; status: string; active_version_no: number | null }> }>(
          "/api/tenant/atlas-reports",
        );
        setReports(
          (data.reports ?? [])
            .filter((r) => r.status !== "archived")
            .map((r) => ({ id: r.report_id, name: r.name, active_version_no: r.active_version_no })),
        );
      } catch {
        // The report choice simply offers nothing; the page path is unaffected.
      }
    })();
  }, []);

  // Load recipient candidates (active tenant members + groups) once.
  useEffect(() => {
    void (async () => {
      try {
        const data = await apiFetch<{ candidates: Candidate[] }>("/api/tenant/rasci-candidates");
        setCandidates(data.candidates ?? []);
      } catch {
        // Picker still works for external-email kind without candidates.
      }
    })();
  }, []);

  // Load widgets when page changes
  useEffect(() => {
    if (!pageId || targetKind !== "page") {
      setWidgets([]);
      setPageIsPublished(true);
      return;
    }
    void (async () => {
      try {
        const data = await apiFetch<{ published: boolean; widgets: PageWidget[] }>(
          `/api/tenant/subscriptions/page-widgets/${encodeURIComponent(pageId)}`,
        );
        setPageIsPublished(data.published);
        setWidgets(data.widgets ?? []);
      } catch (e) {
        toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      }
    })();
    // Pre-fill name from page title
    const pg = pages.find((p) => p.id === pageId);
    if (pg && !name) setName(`${pg.title} subscription`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  // Pre-fill name from the report's name
  useEffect(() => {
    const r = reports.find((x) => x.id === reportId);
    if (r && !name) setName(`${r.name} subscription`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  // Switching the target kind resets the deliveries' content selection and a
  // still-default subject; a typed subject is kept.
  function chooseTargetKind(kind: TargetKind): void {
    if (kind === targetKind) return;
    const previousDefault = defaultSubject(targetKind);
    setTargetKind(kind);
    setDeliveries((prev) =>
      prev.map((d) => ({
        ...d,
        contentKind: kind === "atlas_report" ? "whole_report" : "whole_page",
        widgetIds: [],
        subject: d.subject === previousDefault ? defaultSubject(kind) : d.subject,
      })),
    );
  }

  function updateDelivery(idx: number, patch: Partial<DraftDelivery>): void {
    setDeliveries((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  }
  function addDelivery(): void {
    setDeliveries((prev) => [
      ...prev,
      {
        name: `Audience ${prev.length + 1}`,
        contentKind: targetKind === "atlas_report" ? "whole_report" : "whole_page",
        widgetIds: [],
        recipients: [],
        subject: defaultSubject(targetKind),
        body: "",
      },
    ]);
  }
  function removeDelivery(idx: number): void {
    setDeliveries((prev) => prev.filter((_, i) => i !== idx));
  }

  const targetChosen = targetKind === "page" ? !!pageId && pageIsPublished : !!reportId;
  const canSubmit =
    targetChosen &&
    !!name.trim() &&
    deliveries.length > 0 &&
    deliveries.every(
      (d) =>
        d.name.trim() &&
        d.recipients.length > 0 &&
        recipientsAreComplete(d.recipients) &&
        d.subject.trim() &&
        (d.contentKind !== "widgets" || d.widgetIds.length > 0),
    );

  const submit = useCallback(async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const cadenceConfig = buildCadenceConfig(cadence, time, weeklyDays, monthlyDay, yearlyMonth, yearlyDay);
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        ...(targetKind === "page" ? { page_id: pageId } : { atlas_report_id: reportId }),
        cadence,
        cadence_config: cadenceConfig,
        active_from: activeFrom || null,
        active_until: activeUntil || null,
        deliveries: deliveries.map((d) => ({
          name: d.name.trim(),
          content_selection:
            targetKind === "atlas_report"
              ? { kind: "whole_report" }
              : d.contentKind === "whole_page"
                ? { kind: "whole_page" }
                : { kind: "widgets", widget_ids: d.widgetIds },
          subject_template: d.subject.trim(),
          body_template: d.body.trim() || null,
          recipients: d.recipients.map(recipientToWire),
        })),
      };
      const result = await apiFetch<{ id: string }>("/api/tenant/subscriptions", {
        method: "POST",
        body,
      });
      toast.push({ kind: "success", message: "Subscription created." });
      nav(`/app/schedule/subscriptions/${result.id}`);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }, [
    canSubmit,
    cadence,
    time,
    weeklyDays,
    monthlyDay,
    yearlyMonth,
    yearlyDay,
    name,
    description,
    targetKind,
    pageId,
    reportId,
    activeFrom,
    activeUntil,
    deliveries,
    nav,
    toast,
  ]);

  return (
    <PageLayout title="New subscription">
      {loadingPages ? <LoadingState /> : null}
      {pagesError ? <ErrorState detail={pagesError} /> : null}
      {!loadingPages && !pagesError ? (
        <div style={{ display: "grid", gap: 12 }}>
          {/* Step 1: target + cadence + meta */}
          <div className="card">
            <h3>1. Choose what to deliver and when</h3>
            <div style={{ display: "grid", gap: 10 }}>
              <div className="field">
                <span className="label">Deliver</span>
                <div style={{ display: "flex", gap: 12 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <input type="radio" name="target-kind" checked={targetKind === "page"} onChange={() => chooseTargetKind("page")} />
                    <span>A page</span>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <input type="radio" name="target-kind" checked={targetKind === "atlas_report"} onChange={() => chooseTargetKind("atlas_report")} />
                    <span>An Atlas Report</span>
                  </label>
                </div>
              </div>
              {targetKind === "page" ? (
                <label className="field">
                  <span className="label">Page</span>
                  <select className="select" value={pageId} onChange={(e) => setPageId(e.target.value)}>
                    <option value="">Select a published page…</option>
                    {pages.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.title}
                      </option>
                    ))}
                  </select>
                  <div className="help">Only published pages are eligible.</div>
                </label>
              ) : (
                <label className="field">
                  <span className="label">Atlas Report</span>
                  <select className="select" value={reportId} onChange={(e) => setReportId(e.target.value)}>
                    <option value="">Select an Atlas Report…</option>
                    {reports.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                        {r.active_version_no === null ? " — no approved version yet" : ""}
                      </option>
                    ))}
                  </select>
                  <div className="help">
                    The whole produced workbook is attached to every audience. A report with no approved version can be subscribed to now
                    and resumed once a version is approved.
                  </div>
                </label>
              )}
              <label className="field">
                <span className="label">Name</span>
                <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
              </label>
              <label className="field">
                <span className="label">Description</span>
                <textarea
                  className="input"
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </label>
              <div className="field">
                <span className="label">Cadence</span>
                <div style={{ display: "flex", gap: 8 }}>
                  {(["DAILY", "WEEKLY", "MONTHLY", "YEARLY"] as Cadence[]).map((c) => (
                    <label key={c} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <input type="radio" name="cadence" checked={cadence === c} onChange={() => setCadence(c)} />
                      <span>{c.charAt(0) + c.slice(1).toLowerCase()}</span>
                    </label>
                  ))}
                </div>
              </div>
              <label className="field">
                <span className="label">Time (HH:MM, tenant timezone)</span>
                <input
                  className="input"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  placeholder="07:00"
                  style={{ maxWidth: 120 }}
                />
              </label>
              {cadence === "WEEKLY" ? (
                <div className="field">
                  <span className="label">Days of the week</span>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {WEEKDAYS.map((d) => (
                      <label key={d.code} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <input
                          type="checkbox"
                          checked={weeklyDays.includes(d.code)}
                          onChange={(e) =>
                            setWeeklyDays((prev) =>
                              e.target.checked ? [...prev, d.code] : prev.filter((x) => x !== d.code),
                            )
                          }
                        />
                        {d.label}
                      </label>
                    ))}
                  </div>
                </div>
              ) : null}
              {cadence === "MONTHLY" ? (
                <label className="field">
                  <span className="label">Day of month</span>
                  <select className="select" value={monthlyDay} onChange={(e) => setMonthlyDay(e.target.value)}>
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={String(n)}>
                        {n}
                      </option>
                    ))}
                    <option value="LAST">Last day</option>
                  </select>
                </label>
              ) : null}
              {cadence === "YEARLY" ? (
                <div style={{ display: "flex", gap: 10 }}>
                  <label className="field" style={{ flex: 1 }}>
                    <span className="label">Month</span>
                    <input
                      className="input"
                      type="number"
                      min={1}
                      max={12}
                      value={yearlyMonth}
                      onChange={(e) => setYearlyMonth(Number.parseInt(e.target.value, 10) || 1)}
                    />
                  </label>
                  <label className="field" style={{ flex: 1 }}>
                    <span className="label">Day</span>
                    <input
                      className="input"
                      type="number"
                      min={1}
                      max={28}
                      value={yearlyDay}
                      onChange={(e) => setYearlyDay(Number.parseInt(e.target.value, 10) || 1)}
                    />
                  </label>
                </div>
              ) : null}
              <div style={{ display: "flex", gap: 10 }}>
                <label className="field" style={{ flex: 1 }}>
                  <span className="label">Active from (optional)</span>
                  <input
                    className="input"
                    type="date"
                    value={activeFrom}
                    onChange={(e) => setActiveFrom(e.target.value)}
                  />
                </label>
                <label className="field" style={{ flex: 1 }}>
                  <span className="label">Active until (optional)</span>
                  <input
                    className="input"
                    type="date"
                    value={activeUntil}
                    onChange={(e) => setActiveUntil(e.target.value)}
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Step 2: deliveries */}
          <div className="card">
            <h3>2. Audiences</h3>
            {targetKind === "page" && pageId && !pageIsPublished ? (
              <div className="help">
                Page is not published. Republish to configure widget selection.
              </div>
            ) : null}
            <div style={{ display: "grid", gap: 12 }}>
              {deliveries.map((delivery, idx) => (
                <div key={idx} className="card" style={{ border: "1px solid var(--border)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      className="input"
                      value={delivery.name}
                      onChange={(e) => updateDelivery(idx, { name: e.target.value })}
                      style={{ flex: 1 }}
                    />
                    {deliveries.length > 1 ? (
                      <button
                        type="button"
                        className="btn btnDanger"
                        onClick={() => removeDelivery(idx)}
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                  {targetKind === "atlas_report" ? (
                    <div style={{ marginTop: 10 }}>
                      <span className="label">Content</span>
                      <div className="help">The whole report file, as the build produced it.</div>
                    </div>
                  ) : (
                  <div style={{ marginTop: 10 }}>
                    <span className="label">Content</span>
                    <label style={{ display: "block", marginTop: 4 }}>
                      <input
                        type="checkbox"
                        checked={delivery.contentKind === "whole_page"}
                        onChange={(e) =>
                          updateDelivery(idx, {
                            contentKind: e.target.checked ? "whole_page" : "widgets",
                            widgetIds: e.target.checked ? [] : delivery.widgetIds,
                          })
                        }
                      />
                      <span style={{ marginLeft: 6 }}>Whole page</span>
                    </label>
                    <div className="help">
                      When checked, every widget on the page is included in the email&apos;s
                      XLSX attachment. Uncheck to pick specific widgets to include.
                    </div>
                    {delivery.contentKind === "widgets" && widgets.length > 0 ? (
                      <div style={{ marginTop: 6, paddingLeft: 16 }}>
                        {widgets.map((w) => (
                          <label key={w.id} style={{ display: "block" }}>
                            <input
                              type="checkbox"
                              checked={delivery.widgetIds.includes(w.id)}
                              onChange={(e) =>
                                updateDelivery(idx, {
                                  widgetIds: e.target.checked
                                    ? [...delivery.widgetIds, w.id]
                                    : delivery.widgetIds.filter((id) => id !== w.id),
                                })
                              }
                            />
                            <span style={{ marginLeft: 6 }}>
                              {w.title}{" "}
                              <span className="muted" style={{ fontSize: 12 }}>
                                ({w.kind})
                              </span>
                            </span>
                          </label>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  )}
                  <div style={{ marginTop: 10 }}>
                    <RecipientsEditor
                      recipients={delivery.recipients}
                      candidates={candidates}
                      onChange={(recipients) => updateDelivery(idx, { recipients })}
                    />
                  </div>
                  <label className="field" style={{ marginTop: 10 }}>
                    <span className="label">Subject</span>
                    <input
                      className="input"
                      value={delivery.subject}
                      onChange={(e) => updateDelivery(idx, { subject: e.target.value })}
                    />
                    <SubscriptionVariablesHelp target={targetKind} />
                  </label>
                  <label className="field" style={{ marginTop: 10 }}>
                    <span className="label">Body (optional)</span>
                    <textarea
                      className="input mono"
                      rows={6}
                      value={delivery.body}
                      onChange={(e) => updateDelivery(idx, { body: e.target.value })}
                      placeholder="Leave blank for the default body. Atlas-user recipients see a preference-link footer; external recipients see a contact-the-owner footer."
                    />
                  </label>
                </div>
              ))}
              <div>
                <button type="button" className="btn" onClick={addDelivery}>
                  + Add another audience
                </button>
              </div>
            </div>
          </div>

          <div className="actions">
            <button
              type="button"
              className="btn btnPrimary"
              disabled={!canSubmit || busy}
              onClick={() => void submit()}
            >
              {busy ? "Creating…" : "Create subscription"}
            </button>
            <button type="button" className="btn" onClick={() => nav("/app/schedule/subscriptions")}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </PageLayout>
  );
}
